import React from 'react';
import styled from 'styled-components';

import { Chip, Icon, formatNumber } from '@column/column-ui-kit';

import { CopyInput } from '~/components';
import { CheckScannerIcon } from '~/elements/icons/transfers/CheckScanner';
import { Flex, FormElement, FormLabel, Inner, Text } from '~/styles';
import { formatDate, formatDateTime } from '~/util';

const TransferSummary = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 24px;

  @media (max-width: 900px) {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
  }
`;

const TransferMeta = styled.ul`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 6px 16px;
  list-style: none;
  flex-wrap: wrap;

  li {
    display: flex;
    align-items: center;
  }
`;

const TransferID = styled.div`
  min-width: 310px;
  max-width: 25%;

  @media (max-width: 900px) {
    min-width: 0;
    max-width: 100%;
  }
`;

export const InfoForm = styled.form`
  border-top: 1px solid ${({ theme }) => theme.secondary.blendToBackground(200)};
`;

const DateTimeDisplay = styled.div`
  position: relative;
  display: inline-flex;
  flex-direction: row;
  align-items: center;
  gap: 6px;
`;

const DateTimeLocal = styled.span`
  transition: opacity 0.2s ease-in-out;
  opacity: 1;
  position: absolute;
  left: 0;
`;

const DateTimeUTC = styled(DateTimeLocal)`
  opacity: 0;
  position: relative;
`;

const DayLocal = styled.span`
  position: absolute;
  top: 2px;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 9px;
  font-weight: 600;
  color: ${({ theme }) => theme.secondary.blendToBackground(800)};
  transition: opacity 0.2s ease-in-out;
`;

const DayUTC = styled(DayLocal)`
  opacity: 0;
`;

const DayDisplay = styled.div`
  position: relative;

  .icon-calendar-line {
    display: none;
  }
`;

const Date = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  color: ${({ theme }) => theme.secondary.blendToBackground(800)};

  &:hover {
    ${DateTimeLocal},
    ${DayLocal} {
      opacity: 0;
    }

    ${DateTimeUTC},
    ${DayUTC} {
      opacity: 1;
    }
  }
`;

interface TransferBatchHeaderProps {
  amount: number | string;
  createdAt: string;
  currencyCode?: string;
  transferBatchId: string;
  transferCount: number;
}

export const TransferBatchHeader: React.FC<React.PropsWithChildren<TransferBatchHeaderProps>> = ({
  amount,
  createdAt,
  currencyCode,
  transferBatchId,
  transferCount,
}) => {
  return (
    <Inner px="var(--page-margin)">
      <TransferSummary>
        <Flex flexDirection="column" gap={12}>
          <Flex gap={8}>
            <Flex alignItems="baseline" gap={4}>
              <Text color={Number(amount) > 0 ? 'success' : 'foreground'} size={24} weight="semibold">
                {formatNumber(amount, currencyCode)}
              </Text>
              <Text size={14} color="lighter" weight="medium">
                {currencyCode || 'USD'}
              </Text>
            </Flex>
          </Flex>

          <TransferMeta>
            <li>
              <Chip icon={<CheckScannerIcon />} size="large" variant="subtle">
                {transferCount} {transferCount === 1 ? 'Transfer' : 'Transfers'}
              </Chip>
            </li>
            <li>
              <Date>
                <DayDisplay>
                  <DayLocal>
                    {createdAt
                      ? formatDate(createdAt, { dateFormat: 'short', timeZoneFormat: 'local' })
                          .split(' ')[1]
                          .replace(',', '')
                      : ''}
                  </DayLocal>
                  <DayUTC>
                    {createdAt
                      ? formatDate(createdAt, { dateFormat: 'short', timeZoneFormat: 'utc' })
                          .split(' ')[1]
                          .replace(',', '')
                      : ''}
                  </DayUTC>
                  <Icon.Calendar />
                </DayDisplay>
                <DateTimeDisplay>
                  <DateTimeLocal>
                    <Text size={14} color="currentColor" weight="medium" whiteSpace="nowrap">
                      {formatDateTime(createdAt, { timeZoneFormat: 'local' })}
                    </Text>
                  </DateTimeLocal>
                  <DateTimeUTC>
                    <Text size={14} color="currentColor" weight="medium" whiteSpace="nowrap">
                      {formatDateTime(createdAt, { timeZoneFormat: 'utc' })}
                    </Text>
                  </DateTimeUTC>
                </DateTimeDisplay>
              </Date>
            </li>
          </TransferMeta>
        </Flex>
        <TransferID>
          <FormElement>
            <FormLabel>Batch ID</FormLabel>
            <CopyInput value={transferBatchId} size="small" isMonospaced />
          </FormElement>
        </TransferID>
      </TransferSummary>
    </Inner>
  );
};
